/**
 * Playing the far end.
 *
 * WebRTC hands the console a `MediaStream`, and a stream that is not attached to a playing sink
 * is silent — but not inertly so. Chrome decodes audio on demand, pulled by the sink, so with
 * nothing attached the packets arrive, pile up in the jitter buffer and are flushed:
 * `inbound-rtp.totalSamplesReceived` stays 0, `jitterBufferEmittedCount` stays 0, and
 * `packetsDiscarded` climbs in steps of a couple of hundred. That is the signature to look for
 * in `chrome://webrtc-internals` — bytes arriving with zero samples emitted means "nothing is
 * listening", not "the network is broken".
 *
 * This module owns the one audio element the console plays through, and the parsing of the
 * vendored client's event payload — which is the part that got this wrong once already.
 */

/**
 * Pull the stream out of a `remoteStream` event.
 *
 * The vendored client emits **one object**, `{ callId, stream }` (VertoClient.js, in the
 * `ontrack` handler) — not positional arguments. Reading the wrong index here is silent: the
 * handler runs, finds `undefined`, attaches nothing, and the call connects perfectly with no
 * audio. Hence a named function with a test rather than an index at the call site.
 *
 * Duck-typed rather than `instanceof MediaStream` so this stays unit-testable outside a browser.
 */
export function remoteStreamFrom(args: unknown[]): MediaStream | null {
  const payload = args[0];
  if (!payload || typeof payload !== "object") return null;
  const stream = (payload as { stream?: unknown }).stream;
  if (!stream || typeof stream !== "object") return null;
  if (typeof (stream as MediaStream).getAudioTracks !== "function") return null;
  return stream as MediaStream;
}

export class AutoplayBlocked extends Error {
  constructor() {
    super("The browser blocked audio playback. Click anywhere on the page to enable sound.");
    this.name = "AutoplayBlocked";
  }
}

/**
 * The single `<audio>` element the console plays remote audio through.
 *
 * One element, reused for every call. A fresh element per call is treated by the browser as a
 * fresh autoplay attempt, and after the first one it starts refusing.
 *
 * Because there is one element and a rep can have two calls, the element also has to be *routed*:
 * streams are kept per call and only the active leg's is attached. Assigning `srcObject` for
 * whichever stream arrived most recently — what this did before — silently made the newest call
 * the only one audible, and left the rep hearing nothing at all after swapping back to the leg
 * they had put on hold.
 */
export class RemoteAudio {
  private element: HTMLAudioElement | null = null;
  private streams = new Map<string, MediaStream>();
  private attached: string | null = null;
  /** The speaker the rep picked in the setup dialog, or null for the system default. */
  private sink: string | null = null;

  private ensureElement(): HTMLAudioElement {
    if (this.element) return this.element;
    const el = document.createElement("audio");
    el.autoplay = true;
    // iOS Safari otherwise takes the stream fullscreen.
    el.setAttribute("playsinline", "");
    el.style.display = "none";
    document.body.appendChild(el);
    this.element = el;
    void this.applySink(el);
    return el;
  }

  /**
   * Play through a chosen speaker (`softphone/audio-devices.ts`).
   *
   * Applied to the element that exists as well as remembered for the next one, because a rep who
   * opens the setup dialog mid-call is usually doing it *because* of that call — the far end is
   * coming out of the laptop instead of their headset — and a change that waited for the next
   * call would not answer the question they are asking.
   *
   * Best effort by design: `setSinkId` is Chromium's and recent Firefox's, it rejects for a
   * device that has gone, and in every one of those cases the call keeps playing through the
   * system default. Silence would be the worse failure, so nothing here throws.
   */
  setSink(deviceId: string | null): void {
    this.sink = deviceId;
    if (this.element) void this.applySink(this.element);
  }

  private async applySink(el: HTMLAudioElement): Promise<void> {
    if (typeof el.setSinkId !== "function") return;
    // "" is the browser's own word for the default device; `setSinkId(null)` is a type error.
    try {
      await el.setSinkId(this.sink ?? "");
    } catch (error) {
      console.error("[softphone] could not play through the chosen speaker", error);
    }
  }

  /** Remember a call's stream, and play it if that call is the one we are routed to. */
  async offer(callId: string, stream: MediaStream): Promise<void> {
    this.streams.set(callId, stream);
    // The first stream to arrive wins by default, so a single call needs no routing at all.
    if (this.attached === null || this.attached === callId) {
      this.attached = callId;
      await this.play(stream);
    }
  }

  /** Route the element to a call. No-op when that call has not sent a stream yet. */
  async route(callId: string | null): Promise<void> {
    this.attached = callId;
    if (callId === null) {
      if (this.element) this.element.srcObject = null;
      return;
    }
    const stream = this.streams.get(callId);
    if (stream) {
      await this.play(stream);
      return;
    }
    // Routed to a leg that has not sent audio yet — during hold-and-answer, for a second or so.
    // Leaving the previous leg attached would keep the rep listening to the call they just put
    // on hold, which is worse than the silence they are expecting.
    if (this.element) this.element.srcObject = null;
  }

  /**
   * Drop every stream whose call is no longer live.
   *
   * A `MediaStream` held past the end of its call keeps its tracks — and the memory behind them —
   * alive for as long as the tab is open, and a rep works a list all day.
   */
  retain(liveCallIds: ReadonlySet<string>): void {
    for (const id of [...this.streams.keys()]) {
      if (!liveCallIds.has(id)) this.streams.delete(id);
    }
    if (this.attached !== null && !liveCallIds.has(this.attached)) {
      this.attached = null;
      if (this.element) this.element.srcObject = null;
    }
  }

  /** Attach and start playing. Throws `AutoplayBlocked` when the browser refuses. */
  async play(stream: MediaStream): Promise<void> {
    const el = this.ensureElement();
    if (el.srcObject !== stream) el.srcObject = stream;
    try {
      // `autoplay` alone is not enough: it is evaluated when the element gets a source, and a
      // srcObject assigned later can miss it. Calling play() explicitly is what actually starts
      // the sink — and its rejection is the only way to learn the browser said no.
      await el.play();
    } catch (error) {
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        throw new AutoplayBlocked();
      }
      throw error;
    }
  }

  /** Retry after the user has interacted with the page. */
  async resume(): Promise<void> {
    if (this.element?.srcObject) await this.play(this.element.srcObject as MediaStream);
  }

  dispose(): void {
    this.streams.clear();
    this.attached = null;
    if (!this.element) return;
    this.element.pause();
    this.element.srcObject = null;
    this.element.remove();
    this.element = null;
  }
}
